import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Avatar, IconButton } from "@mui/material";
import { useFormik } from "formik";
import MUITextField from "../../../components/MUITextField/MUITextField";
import MUIButton from "../../../components/MUIButton/MUIButton";
import { AppGlobalContext } from "../../../contexts/AppContext.context";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import userManagementService from "../../../services/UserManagementService";
import { languagesForContent } from "../../../config/App.config";
import { uploadImageHandler } from "../../../common/UploadImage/uploadImageHandler";
import { AppTheme } from "../../../theme/App.theme";
import { Icons } from "../../../config/MuiIcons.config";
import useLogoutHook from "../../../hook/useLogoutHook";
import { CLOUD_STOAGE } from "../../../config/CloudStorage.config";

const UpdateOrDeleteProfile = ({ Data = {}, CloseDrawer = () => {} }) => {
  const { SetAppState } = useContext(AppGlobalContext);
  const { showSnackbar } = useSnackbar();
  const { handleUserLogout } = useLogoutHook();

  const fileInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null); // { url, file, name }
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ isError: false, message: "" });

  const user = useMemo(() => Data || {}, [Data]);

  const languageOptions = useMemo(
    () => languagesForContent.map((l, idx) => ({ id: `lang-${idx}`, value: l.value, label: l.label })),
    []
  );

  const handleUpdateProfile = async (values) => {
    try {
      setSubmitLoading(true);

      const uid = user?.uid;
      if (!uid) {
        showSnackbar("Invalid user. Please re-login.", "error");
        return;
      }

      // Upload image if a new blob/file is selected
      let profileImageUrl = user?.profileImage || null;
      if (photoPreview?.file) {
        const uploadedUrl = await new Promise((resolve, reject) => {
          uploadImageHandler(
            CLOUD_STOAGE.PROFILE_IMAGES,
            `${Date.now()}`,
            photoPreview.file,
            (url) => resolve(url),
            setUploadingImage,
            setUploadStatus,
            () => {}
          );
        });
        profileImageUrl = uploadedUrl;
      }

      const payload = {
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        preferredLanguage: values.preferredLanguage,
        profileImage: profileImageUrl,
      };

      const { statusCode } = await userManagementService.updateById(
        uid,
        payload
      );
      if (statusCode !== 200) {
        showSnackbar("Failed to update profile. Please try again.", "error");
        return;
      }

      // Update global app state so UI reflects new values
      SetAppState({
        authUserDetails: {
          ...user,
          ...payload,
        },
      });

      showSnackbar("Profile updated successfully.", "success");
      CloseDrawer();
    } catch (err) {
      showSnackbar("Unable to update profile. Please try again.", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      preferredLanguage: user?.preferredLanguage || "",
      email: user?.email || "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.firstName?.trim()) errors.firstName = "First name is required";
      if (!values.preferredLanguage) errors.preferredLanguage = "Please select a language";
      return errors;
    },
    onSubmit: async (values) => {
      handleUpdateProfile(values);
    },
  });

  useEffect(() => {
    // Preload current image for preview (non-blob URL)
    if (user?.profileImage) {
      setPhotoPreview((prev) => prev || { url: user.profileImage, file: null, name: "profile" });
    }
  }, [user?.profileImage]);

  const handlePickPhoto = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f); // blob URL for preview
    setPhotoPreview({ url, file: f, name: f.name });
  };

  const handleDeleteProfile = async () => {
    const uid = user?.uid;
    if (!uid) return;
    const ok = window.confirm("Are you sure you want to delete your profile? This action cannot be undone.");
    if (!ok) return;
    try {
      setSubmitLoading(true);
      const { statusCode } = await userManagementService.deleteById(uid);
      if (statusCode !== 200) {
        showSnackbar("Failed to delete profile. Please try again.", "error");
        return;
      }
      showSnackbar("Profile deleted.", "success");
      CloseDrawer();
      // Also log the user out after deletion
      await handleUserLogout();
    } catch (_) {
      showSnackbar("Unable to delete profile. Please try again.", "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Photo section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          mb: 3,
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Avatar
            src={photoPreview?.url || ""}
            alt={(formik.values.firstName || "User") + " " + (formik.values.lastName || "")}
            sx={{
              width: { xs: 88, sm: 96, md: 112 },
              height: { xs: 88, sm: 96, md: 112 },
              bgcolor: AppTheme.BORDER_COLOR,
              fontSize: 28,
              border: `3px solid ${AppTheme.PRIMARY_COLOR}`,
            }}
          >
            {(formik.values.firstName || "U").charAt(0)}
          </Avatar>
          <IconButton
            size="small"
            onClick={handlePickPhoto}
            sx={{
              position: "absolute",
              bottom: 6,
              right: 6,
              bgcolor: AppTheme.PRIMARY_COLOR,
              color: "#fff",
              "&:hover": { bgcolor: AppTheme.PRIMARY_COLOR },
              width: 34,
              height: 34,
            }}
          >
            <Icons.ModeEditOutlineOutlinedIcon fontSize="small" />
          </IconButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Edit Photo
        </Typography>
      </Box>

      {/* Title/Subtitle */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Typography sx={{ fontSize: "22px", fontWeight: 700 }}>Update Your Profile</Typography>
        <Typography variant="body1" color="text.secondary">
          Update your name and photo here.
        </Typography>
      </Box>

      {/* Form */}
      <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.5 }}>
          <div>
            <Typography variant="subtitle2" gutterBottom>
              First Name
            </Typography>
            <MUITextField
              name="firstName"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              placeholder="First Name"
            />
          </div>

          <div>
            <Typography variant="subtitle2" gutterBottom>
              Last Name
            </Typography>
            <MUITextField
              name="lastName"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Last Name"
            />
          </div>

          <div>
            <Typography variant="subtitle2" gutterBottom>
              Preferred Language
            </Typography>
            <MUITextField
              type="select"
              name="preferredLanguage"
              value={formik.values.preferredLanguage}
              onChange={(e) => formik.setFieldValue("preferredLanguage", e.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.preferredLanguage && Boolean(formik.errors.preferredLanguage)}
              helperText={formik.touched.preferredLanguage && formik.errors.preferredLanguage}
              placeholder="Select Language"
              options={languageOptions}
            />
          </div>
        </Box>

        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <MUIButton
            primary
            isLoadingButton
            isLoading={submitLoading || uploadingImage}
            type="submit"
            fullWidth
          >
            Save Changes
          </MUIButton>

          <Typography
            variant="body2"
            align="center"
            onClick={handleDeleteProfile}
            sx={{ cursor: "pointer", color: AppTheme.PRIMARY_COLOR, fontWeight: 600 }}
          >
            Delete Profile
          </Typography>
        </Box>
      </form>
    </div>
  );
};

export default UpdateOrDeleteProfile;